/**
 * §14a control figures (Schaltengine) — mirrors backend Models/Config/ControlConstants.cs 1:1; change both
 * together. Rules and sources: doc/Architektur/STEUERUNG.md (3.1–3.6, decisions 7.2). Operator-tunable values
 * live in the settings section `controlSettings` and take their defaults from here.
 */
import { LOADING_WARNING_PCT } from './loadingConstants';
import { NOMINAL_VOLTAGE_LN_V, PU_BASE_LN_V } from './electricalConstants';

/** Gefährdung ab 80 % der BETRIEBLICHEN Dauerstrombelastbarkeit (FNN Hinweis 2c) — rated capacity × Betriebsfaktor. */
export const CONTROL_TRIGGER_LOADING_PCT = LOADING_WARNING_PCT;

/** 80 % of the permissible 10 % voltage drop = 8 % Un (FNN 2c/2e). */
export const CONTROL_UNDERVOLTAGE_TRIGGER_PCT = 8;

/** Trigger voltage L-N: Un − 8 % = 211,6 V (reference Un = 230 V, never the pu base). */
export const CONTROL_UNDERVOLTAGE_TRIGGER_V = NOMINAL_VOLTAGE_LN_V * (1 - CONTROL_UNDERVOLTAGE_TRIGGER_PCT / 100);

/** The same trigger in load-flow per-unit (base 400 V/√3) — ≈ 0,916 pu, never 0,92. */
export const CONTROL_UNDERVOLTAGE_TRIGGER_PU = CONTROL_UNDERVOLTAGE_TRIGGER_V / PU_BASE_LN_V;

/** Betriebsfaktor of an element without a maintained value, and its accepted range (DB CHECK, migration 0061). */
export const OPERATIONAL_RATING_FACTOR_DEFAULT = 1;
export const OPERATIONAL_RATING_FACTOR_MIN = 0.3;
export const OPERATIONAL_RATING_FACTOR_MAX = 1;

/** At most 5 min from the grid-state result to the command at the MSB (FNN 2g). */
export const CONTROL_NZE_VALIDITY_MINUTES = 5;

/** Control and release in 15-minute slots aligned to the quarter hour (BK6-22-300 Ziff. 4.3). */
export const CONTROL_SLOT_MINUTES = 15;

/** The MSB must hold a command at least 60 s before its effective period (FNN 2c). */
export const CONTROL_MSB_LEAD_TIME_SECONDS = 60;

/** Start times are spread over this many groups (only against simultaneous switching). */
export const CONTROL_START_GROUP_COUNT = 4;
export const CONTROL_GROUP_START_OFFSET_SECONDS_DEFAULT = 15;

/** Start factor when the requirement cannot be sized (STEUERUNG.md 7.2 Nr. 13) — mirror of ControlConstants.FallbackStartFactorDefault. */
export const CONTROL_FALLBACK_START_FACTOR_DEFAULT = 0.5;

/** Guaranteed minimum power per SteuVE (BK6-22-300 Ziff. 4.5) — mirror of GzfConstants.StandardMinPowerKw. */
export const CONTROL_STANDARD_MIN_POWER_KW = 4.2;

/** Large heat pumps / cooling (> 11 kW) keep this share of their power (Ziff. 4.5.1) — mirror of GzfConstants.LargeDeviceMinFraction. */
export const CONTROL_LARGE_DEVICE_MIN_PCT = 40;

/** Minutes without automatic execution after a Not-Aus (STEUERUNG.md 7.2 Nr. 14) — mirror of ControlConstants.ManualAfterNotAusMinutesDefault. */
export const CONTROL_MANUAL_AFTER_NOT_AUS_MINUTES_DEFAULT = 60;

/** Default steps of the stepwise release (STEUERUNG.md 7.2 Nr. 12) — mirror of ControlConstants.ReleaseStepsDefault. */
export const CONTROL_RELEASE_STEPS_DEFAULT = 3;

/** Safety margin on the required reduction (devices without load, communication faults). */
export const CONTROL_SAFETY_MARGIN_PCT_DEFAULT = 10;

/** 1-minute power log until 10 min after a command (FNN 2b), kept at least 2 years (Ziff. 7.3). */
export const CONTROL_POWER_LOG_AFTER_END_MINUTES = 10;
export const CONTROL_POWER_LOG_RETENTION_YEARS = 2;

/** Effective §14a loading trigger of an element in % of its rated capacity. */
export function controlTriggerLoadingPct(operationalRatingFactor: number | null | undefined): number {
  return CONTROL_TRIGGER_LOADING_PCT * (operationalRatingFactor ?? OPERATIONAL_RATING_FACTOR_DEFAULT);
}

/** True when the value may be stored as Betriebsfaktor. */
export function isValidOperationalRatingFactor(value: number): boolean {
  return Number.isFinite(value) && value >= OPERATIONAL_RATING_FACTOR_MIN && value <= OPERATIONAL_RATING_FACTOR_MAX;
}
